import { useParams } from 'react-router-dom';
import { useToast } from '@/hooks/useToast';
import useDialog, { DialogType } from '@/hooks/useDialog';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Form, FormField } from '@/components/ui/form';
import { useController, useForm, useWatch } from 'react-hook-form';
import { TOAST_CONST } from '@/src/constants/toast';
import { zodResolver } from '@hookform/resolvers/zod';
import FormItem from '@/components/molecule/FormItem';
import React, { useEffect, useMemo } from 'react';
import { CustomersDto } from '@/src/models/CustomersDto';
import useAuthStore from '@/src/store/useAuthStore';
import { PostCreditHistoriesRequestBody } from '@/src/apis/rest/creditHistory.dtos';
import { postCreditHistoriesAPI } from '@/src/apis/rest/creditHistory';
import * as creditField from '@/src/pages/Customer/creditField';
import { getCreditTemplateAPI } from '@/src/apis/rest/creditTemplate';
import ConfirmDialog from '@/components/organism/ConfirmDialog';
import { UNIT_STRINGS } from '@/src/constants/unit';
import { formatter } from '@/src/utils/formatter';
import FormContent from '@/src/components/molecule/FormContent';
import Input from '@/src/components/atom/Input';
import Select from '@/src/components/atom/Select';

interface CreditManageDialogProps {
  customer: CustomersDto | undefined;
  submitCallback?: () => Promise<any>;
  isOpen: boolean;
  setOpen: (open: boolean) => void;
}

function CreditManageDialog({
  customer,
  submitCallback,
  isOpen = false,
  setOpen,
}: CreditManageDialogProps) {
  const { toast } = useToast();
  const { id } = useParams();
  const auth = useAuthStore(state => state.auth);

  const {
    Dialog: WarningDialog,
    isOpen: isWarningDialogOpen,
    openDialog: openWarningDialog,
    closeDialog: closeWarningDialog,
  } = useDialog({
    type: DialogType.ALERT,
  });

  const creditForm = useForm<PostCreditHistoriesRequestBody>({
    resolver: zodResolver(creditField.formSchema()),
    defaultValues: {
      ...creditField.DEFAULT_VALUES,
      creditTemplateId: undefined,
    },
  });

  const valueController = useController({
    name: 'value',
    control: creditForm.control,
  });

  const watchFields = useWatch({ control: creditForm.control });

  const { data: creditTemplateData } = useQuery({
    queryKey: ['creditTemplate'],
    queryFn: () =>
      getCreditTemplateAPI({
        page: 1,
        pageSize: 9999,
        category: '관리자 지정',
        isActive: true,
      }),
  });

  const postCreditHistories = useMutation({
    mutationKey: ['customers', 'normal', id],
    mutationFn: (requestBody: PostCreditHistoriesRequestBody) =>
      postCreditHistoriesAPI(requestBody),
  });

  const isFormValid = useMemo(() => {
    const creditIdRequired = watchFields.creditTemplateId;
    const descRequired = watchFields.description !== '';
    return descRequired && creditIdRequired;
  }, [watchFields]);

  const handleStatusSubmit = (requestBody: PostCreditHistoriesRequestBody) => {
    const curCredit = customer?.credit?.currentCredit || 0;
    const selectedCreditId = Number(requestBody.creditTemplateId);
    const selectedCreditValue =
      creditTemplateData?.rows.find(p => Number(p.id) === selectedCreditId)
        ?.credit || 0;

    if (requestBody.type === '차감' && selectedCreditValue > curCredit) {
      openWarningDialog();
      return;
    }

    if (requestBody.creditTemplateId?.toString() === '-1') {
      delete requestBody.creditTemplateId;
    }

    postCreditHistories.mutate(
      {
        ...requestBody,
        customerId: Number(id),
      },
      {
        onSuccess: async () => {
          await submitCallback?.();
          creditForm.reset();
          toast({
            variant: 'success',
            title: TOAST_CONST.saveSuccess.title,
            description: TOAST_CONST.saveSuccess.description,
          });
        },
        onError: e => {
          creditForm.reset();
          toast({
            variant: 'error',
            title: TOAST_CONST.error.title,
            description: e.message,
          });
        },
      },
    );
  };

  useEffect(() => {
    if (customer && auth && isOpen) {
      creditForm.setValue(creditField.formFields.nick.name, customer.nick);
      creditForm.setValue(
        creditField.formFields.currentCredit.name,
        `${customer?.credit?.currentCredit.toLocaleString() || 0}${
          UNIT_STRINGS.CREDIT
        }`,
      );
      creditForm.setValue(creditField.formFields.administrator.name, auth.name);
    }

    return () => {
      creditForm.reset();
    };
  }, [isOpen, auth, creditForm, customer]);

  return (
    <>
      <WarningDialog
        title="크레딧 차감 불가"
        open={isWarningDialogOpen}
        onOpenChange={closeWarningDialog}
        cancelButtonProps={{ className: 'hidden' }}
      >
        보유한 크레딧보다 차감할 크레딧가 더 많아 차감이 불가합니다
      </WarningDialog>
      <ConfirmDialog
        title="크레딧 지급/차감 관리"
        open={isOpen}
        onOpenChange={setOpen}
        onOk={() => handleStatusSubmit(creditForm.getValues())}
        okButtonProps={{ disabled: !isFormValid }}
        isLoading={postCreditHistories.isPending}
      >
        <Form {...creditForm}>
          <form className="flex flex-col gap-[24px]">
            <FormField
              control={creditForm.control}
              name={creditField.formFields.nick.name}
              render={({ field, fieldState: { error } }) => {
                return (
                  <FormItem
                    label={creditField.formFields.nick.label}
                    labelSize="M"
                    name={creditField.formFields.nick.name}
                    type="text"
                    control={creditForm.control}
                    direction="vertical"
                    inputProps={{
                      type: 'text',
                      disabled: true,
                      ...field,
                    }}
                    errorMessage={error?.message}
                  />
                );
              }}
            />
            <FormField
              control={creditForm.control}
              name={creditField.formFields.currentCredit.name}
              render={({ field }) => {
                return (
                  <FormItem
                    label={creditField.formFields.currentCredit.label}
                    labelSize="M"
                    name={creditField.formFields.currentCredit.name}
                    type="text"
                    control={creditForm.control}
                    direction="vertical"
                    inputProps={{
                      type: 'text',
                      disabled: true,
                      value: field.value,
                    }}
                  />
                );
              }}
            />
            <FormField
              control={creditForm.control}
              name={creditField.formFields.type.name}
              render={() => {
                return (
                  <FormItem
                    label={creditField.formFields.type.label}
                    labelSize="M"
                    name={creditField.formFields.type.name}
                    type="select"
                    control={creditForm.control}
                    direction="vertical"
                    optionList={creditField.creditTypeOptions.map(
                      creditType => ({
                        label: creditType.label,
                        value: creditType.value,
                      }),
                    )}
                  />
                );
              }}
            />
            <FormField
              control={creditForm.control}
              name={creditField.formFields.creditTemplateId.name}
              render={({ field, fieldState: { error } }) => {
                return (
                  <FormContent
                    isRequired
                    label={creditField.formFields.creditTemplateId.label}
                    error={error}
                    direction="vertical"
                  >
                    <Select
                      option={[
                        {
                          optionList: [
                            ...(creditTemplateData?.rows || []).map(
                              teamplate => ({
                                label: `[관리자 지정] ${teamplate.title} (${formatter.NUM_COMMA(teamplate.credit)}${UNIT_STRINGS.CREDIT})`,
                                value: teamplate.id.toString(),
                              }),
                            ),
                            {
                              label: '직접 입력',
                              value: '-1',
                            },
                          ],
                        },
                      ]}
                      rootProps={{
                        value: field.value?.toString(),
                        onValueChange: value => {
                          field.onChange(value);
                          valueController.field.onChange('');
                        },
                      }}
                      valueProps={{
                        placeholder:
                          creditField.formFields.creditTemplateId.placeholder,
                      }}
                    />
                  </FormContent>
                );
              }}
            />
            {creditForm.watch('creditTemplateId')?.toString() === '-1' && (
              <FormField
                control={creditForm.control}
                name={creditField.formFields.value.name}
                render={({ field, fieldState: { error } }) => {
                  return (
                    <FormContent
                      className="w-full flex-col"
                      label={creditField.formFields.value.label}
                      isRequired
                      error={error}
                      direction="vertical"
                    >
                      <Input
                        type="number"
                        min={0}
                        containerClassName="flex-1"
                        placeholder={creditField.formFields.value.placeholder}
                        onClear={() => field.onChange('')}
                        {...field}
                      />
                    </FormContent>
                  );
                }}
              />
            )}
            <FormField
              control={creditForm.control}
              name={creditField.formFields.subDescription.name}
              render={({ field, fieldState: { error } }) => {
                return (
                  <FormItem
                    label={creditField.formFields.subDescription.label}
                    labelSize="M"
                    name={creditField.formFields.subDescription.name}
                    type="textArea"
                    control={creditForm.control}
                    direction="vertical"
                    errorMessage={error?.message}
                    rules={{
                      required: true,
                    }}
                    inputProps={{
                      className: 'h-[140px]',
                      placeholder:
                        creditField.formFields.subDescription.placeholder,
                      value: field.value,
                      onChange: e => field.onChange(e),
                    }}
                  />
                );
              }}
            />
            <FormField
              control={creditForm.control}
              name={creditField.formFields.administrator.name}
              render={({ field, fieldState: { error } }) => {
                return (
                  <FormItem
                    label={creditField.formFields.administrator.label}
                    labelSize="M"
                    name={creditField.formFields.administrator.name}
                    type="text"
                    control={creditForm.control}
                    direction="vertical"
                    inputProps={{
                      type: 'text',
                      disabled: true,
                      ...field,
                    }}
                    errorMessage={error?.message}
                  />
                );
              }}
            />
          </form>
        </Form>
      </ConfirmDialog>
    </>
  );
}

export default React.memo(CreditManageDialog);
